import type {
  CreatorResourceUsage,
  PublishedGame,
  PublicationDurationKey,
  PublicationSummary,
} from '../../shared/publications';
import { ApiRequestError } from '../auth/authApi';
import type { PublicationEditInput } from '../../shared/publicationEditing';
import type {
  FinishPlayAttemptInput,
  PublicLeaderboardEntry,
  StartPlayAttemptInput,
  TeacherResult,
  TeacherResultGroup,
} from '../../shared/results';

type ErrorPayload = {
  error?: { code: string; message: string; fields?: Record<string, string> };
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/odevlendir/api${path}`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    ...init,
  });
  if (response.ok) return (await response.json()) as T;
  const payload = response.headers.get('content-type')?.includes('application/json')
    ? ((await response.json()) as ErrorPayload)
    : undefined;
  throw new ApiRequestError(response.status, payload?.error);
}

export async function listPublications(): Promise<{
  publications: PublicationSummary[];
  usage: CreatorResourceUsage;
}> {
  const payload = await request<{
    success: true;
    data: { publications: PublicationSummary[]; usage: CreatorResourceUsage };
  }>('/publications');
  return payload.data;
}

export async function publishDraft(
  draftId: string,
  duration: PublicationDurationKey,
): Promise<PublicationSummary> {
  const payload = await request<{ success: true; data: { publication: PublicationSummary } }>(
    `/drafts/${encodeURIComponent(draftId)}/publications`,
    { method: 'POST', body: JSON.stringify({ duration }) },
  );
  return payload.data.publication;
}

export async function unpublishPublication(publicationId: string): Promise<void> {
  await request(`/publications/${encodeURIComponent(publicationId)}`, { method: 'DELETE' });
}

export type EditablePublication = { publication: PublicationSummary; game: PublishedGame };

export async function getEditablePublication(id: string): Promise<EditablePublication> {
  const payload = await request<{ success: true; data: EditablePublication }>(`/publications/${encodeURIComponent(id)}`);
  return payload.data;
}

export async function updatePublication(id: string, input: PublicationEditInput): Promise<EditablePublication> {
  const payload = await request<{ success: true; data: EditablePublication }>(`/publications/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(input) });
  return payload.data;
}

export async function getPublishedGame(slug: string): Promise<PublishedGame> {
  const payload = await request<{ success: true; data: { game: PublishedGame } }>(
    `/play/${encodeURIComponent(slug)}`,
  );
  return payload.data.game;
}

export async function startPlayAttempt(slug: string, player: StartPlayAttemptInput): Promise<{ id: string; startedAt: number; game: PublishedGame }> {
  const payload = await request<{ success: true; data: { attempt: { id: string; startedAt: number; game: PublishedGame } } }>(
    `/play/${encodeURIComponent(slug)}/attempts`,
    { method: 'POST', body: JSON.stringify(player) },
  );
  return payload.data.attempt;
}

export async function finishPlayAttempt(
  slug: string,
  result: FinishPlayAttemptInput,
): Promise<{ score: number; durationMs: number; completed: boolean }> {
  const payload = await request<{
    success: true;
    data: { result: { score: number; durationMs: number; completed: boolean } };
  }>(
    `/play/${encodeURIComponent(slug)}/attempts/complete`,
    { method: 'POST', body: JSON.stringify(result) },
  );
  return payload.data.result;
}

export async function getLeaderboard(slug: string): Promise<PublicLeaderboardEntry[]> {
  const payload = await request<{
    success: true;
    data: { leaderboard: PublicLeaderboardEntry[] };
  }>(`/play/${encodeURIComponent(slug)}/leaderboard`);
  return payload.data.leaderboard;
}

export async function listTeacherResults(offset = 0, publicationId?: string): Promise<{
  results: TeacherResult[];
  hasMore: boolean;
}> {
  const payload = await request<{
    success: true;
    data: { results: TeacherResult[]; hasMore: boolean };
  }>(`/results?limit=100&offset=${offset}${publicationId ? `&publicationId=${encodeURIComponent(publicationId)}` : ''}`);
  return payload.data;
}

export async function listTeacherResultGroups(offset = 0): Promise<{
  groups: TeacherResultGroup[];
  hasMore: boolean;
}> {
  const payload = await request<{
    success: true;
    data: { groups: TeacherResultGroup[]; hasMore: boolean };
  }>(`/result-groups?limit=50&offset=${offset}`);
  return payload.data;
}
